import { t } from './i18n';
import React, { useState, useEffect } from 'react';
import { api, isSukkahSeason, isPesachSeason, DATED_TYPES, SUBTYPE_OPTIONS, COMMUNITY_OPTIONS, KOSHER_LEVELS, MIKVAH_LEVELS } from './api';
import CategoryToc from './CategoryToc';

const COLORS = {
  primary: '#1a4d8f', text: '#1f2937', muted: '#6b7280', border: '#e5e7eb', bg: '#f7f8fa',
};

const card = { background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 14, marginBottom: 14 };
const head = { fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 };

// Left rail for the list page: the within-results text
// filter, the closed/no-longer-kosher toggle, and the "jump to category" list.
// Stays visible across loading / empty / results states (it's rendered outside
// the results branch) so the filter box never disappears on a 0-result query.
export default function ListSidebar({ filters, updateFilter, categories, results, sortMode, onJumpCategory, onClosest, near = {} }) {
  const { country, city } = filters;
  // `near` is a fresh object every render, so it cannot be a dependency
  // directly — compared by identity it would refetch forever. Its value
  // is what matters.
  const nearKey = JSON.stringify(near);
  // Who vouches for places here. Scoped to the country/city in view, so the
  // dropdown offers Rabbi Zalmanov in Afula and Chabad Piazza Bologna in Rome
  // rather than every recommender in the world.
  const [recommenders, setRecommenders] = useState([]);
  useEffect(() => {
    let live = true;
    api.recommenders({ country, city, ...near })
      .then((r) => { if (live) setRecommenders(Array.isArray(r) ? r : []); })
      .catch(() => { if (live) setRecommenders([]); });
    return () => { live = false; };
  }, [country, city, nearKey]);

  // The kosher standards kept here — Mehadrin, Lubavitch Shechita, Chalav
  // Israel and so on. Same scoping as the recommenders: the menu lists what
  // this country/city actually has, with counts, so nothing offered is empty.
  const [kosherLevels, setKosherLevels] = useState([]);
  useEffect(() => {
    let live = true;
    api.kosherLevels({ country, city, ...near })
      .then((r) => { if (live) setKosherLevels(Array.isArray(r) ? r : []); })
      .catch(() => { if (live) setKosherLevels([]); });
    return () => { live = false; };
  }, [country, city, nearKey]);

  // Nusach, food kind and community.
  //
  // All three already existed in the top filter row — but only AFTER a
  // category was picked, which is the one moment you least need them: a page
  // already narrowed to Synagogues is a short list. Asked from the rail with
  // no category chosen, they are the question a visitor actually arrives
  // with ("is there a dairy place here?", "is there an Ashkenaz minyan?").
  //
  // Each menu is fetched for ITS categories whatever the page is showing, and
  // all of them write `subtype`/`community`, which the results, the count and
  // the category menu all honour. Meat/Dairy/Parve spans four categories, so
  // /subtypes takes them comma-separated and answers once.
  const FOOD_LIST = ['kosher_restaurant', 'kosher_eatery', 'kosher_bakery', 'kosher_catering'];
  const FOOD_TYPES = FOOD_LIST.join(',');
  const type = filters.type || '';
  // `type` carries one category or SEVERAL, comma-separated — the tick-boxes
  // in the category list write the latter, and the backend ORs them. Every
  // test below works on the set, because `type === 'synagogue_shul'` quietly
  // becomes false the moment a second box is ticked, and the nusach menu
  // would vanish just as someone narrowed to shuls and restaurants.
  const pickedTypes = type ? type.split(',').map((s) => s.trim()).filter(Boolean) : [];
  const toggleType = (t2) => {
    const next = pickedTypes.includes(t2) ? pickedTypes.filter((x) => x !== t2) : [...pickedTypes, t2];
    updateFilter('type', next.join(','));
  };

  const none = pickedTypes.length === 0;
  const isShul = none || pickedTypes.includes('synagogue_shul');
  const foodPicked = pickedTypes.filter((x) => FOOD_LIST.includes(x));
  const isFood = none || foodPicked.length > 0;
  // Other categories (stores, hotels, mikvaot) have a subtype vocabulary of
  // their own — Butcher, B&B, Women — worth a dropdown once that category is
  // chosen ALONE, but a junk drawer of 141 values across all of them, and
  // ambiguous across two.
  const otherPicked = pickedTypes.filter((x) => x !== 'synagogue_shul' && !FOOD_LIST.includes(x));
  const isOther = pickedTypes.length === 1 && otherPicked.length === 1;
  // Ask /subtypes only for the food categories actually in play.
  const foodScope = foodPicked.length ? foodPicked.join(',') : FOOD_TYPES;
  // Plain strings for the effect's dependency list — an array literal there
  // is a new object every render, so the fetches would never settle.
  const foodOne = foodPicked.length === 1 ? foodPicked[0] : undefined;
  const otherType = isOther ? otherPicked[0] : null;
  // A mikvah's "kosher standard" is not a food standard: it is how the
  // bor is built — Bor al gabei bor, Bor min hatzad, Hashaka, Zria, Mei
  // Gheshamim. Those two vocabularies were being offered in one list, so
  // somebody filtering restaurants saw 460 places described by the shape
  // of their immersion pool. The edit form has always kept them apart;
  // this is the same split, read from the same two constants.
  const isMikvah = pickedTypes.length === 1 && pickedTypes[0] === 'mikvah_ritual_bath';

  const [nusachim, setNusachim] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [otherSubtypes, setOtherSubtypes] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  useEffect(() => {
    let live = true;
    const set = (fn) => (r) => { if (live) fn(Array.isArray(r) ? r : []); };
    const fail = (fn) => () => { if (live) fn([]); };
    // THE RAIL HAS TO BE SCOPED BY DISTANCE TOO. The category menu above
    // it was showing "Bakery 2107" over a list of four places near the
    // Rome ghetto; these dropdowns had exactly the same fault, offering
    // "Dairy 4364" and "Kosharot 2154" beside the same four. A filter
    // that counts the whole directory is not a filter for this list.
    const scope = { country, city, area: filters.area, ...near };
    if (isShul) {
      api.subtypes({ ...scope, type: 'synagogue_shul' }).then(set(setNusachim)).catch(fail(setNusachim));
      api.communities({ ...scope, type: 'synagogue_shul' }).then(set(setCommunities)).catch(fail(setCommunities));
    } else { setNusachim([]); setCommunities([]); }
    if (isFood) {
      // Scoped to the chosen category when there is one, so the counts in the
      // menu are the counts the results will return.
      api.subtypes({ ...scope, type: foodScope }).then(set(setKinds)).catch(fail(setKinds));
      api.cuisines({ ...scope, type: foodOne }).then(set(setCuisines)).catch(fail(setCuisines));
    } else { setKinds([]); setCuisines([]); }
    if (otherType) api.subtypes({ ...scope, type: otherType }).then(set(setOtherSubtypes)).catch(fail(setOtherSubtypes));
    else setOtherSubtypes([]);
    return () => { live = false; };
  }, [country, city, filters.area, isShul, isFood, foodScope, foodOne, otherType, nearKey]);

  // ONLY WHAT THE ADMIN OFFERS. /subtypes returns the distinct values
  // actually present in the data, and the data has picked up values that
  // belong to another category or to nobody: the food filter was
  // offering Supermarket, Butcher, Hotel, Ari, Ashkenaz, B2B, Wine and
  // one reading "Affordable Kosher Catering for Every Celebration", and
  // the nusach filter was offering Dairy, Meat, Men, Women and
  // "Israelitische Religionsgesellschaft Zurich". Sorting those to the
  // bottom, which is what this did before, still puts them in the menu.
  //
  // SUBTYPE_OPTIONS is the list the edit form writes from, so it is the
  // definition of a real value. Anything else is a record that needs
  // correcting, not an option to offer a visitor.
  // Keep only the options the admin form can actually produce.
  const onlyIn = (options, vocabList, valueOf) => {
    const vocab = (vocabList || []).map((v) => String(v).toLowerCase());
    if (!vocab.length) return options;
    return options.filter((o) => vocab.includes(String(valueOf(o) || '').trim().toLowerCase()));
  };
  const listed = (options, type, map = SUBTYPE_OPTIONS, valueOf = (o) => o.subtype) =>
    onlyIn(options, map[type], valueOf);

  // Meat / Dairy / Parve in that order.
  const KIND_ORDER = ['Meat', 'Dairy', 'Parve'];
  const foodKinds = listed(kinds, 'kosher_restaurant').sort((a, b) => {
    const ai = KIND_ORDER.indexOf(a.subtype); const bi = KIND_ORDER.indexOf(b.subtype);
    if (ai !== bi) return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    return b.count - a.count;
  });
  // One dropdown builder — they are all the same control over a different
  // list, and writing them out five times is how they drift apart.
  const selectStyle = { width: '100%', padding: '9px 10px', fontSize: 14, borderRadius: 8, border: `1px solid ${COLORS.border}`, background: 'white', color: COLORS.text, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
  const Picker = ({ show, label, aria, options, valueOf, current, onPick }) => {
    if (!show || !options.length) return null;
    // A value set by a SIBLING dropdown (nusach chosen, then the meat/dairy
    // menu rendered) must not appear selected here — both write `subtype`.
    const selected = options.some((o) => valueOf(o) === current) ? current : '';
    return (
      <div style={{ marginBottom: 12 }}>
        <select aria-label={aria} value={selected} onChange={(e) => onPick(e.target.value)} style={selectStyle}>
          <option value="">{label}</option>
          {options.map((o) => (
            <option key={valueOf(o)} value={valueOf(o)}>{t(valueOf(o))} ({o.count})</option>
          ))}
        </select>
      </div>
    );
  };
  // SEVERAL AT ONCE, for the three filters where one answer is not
  // enough: which hechsher, which standard, which cuisine. Someone who
  // accepts two batei din, or will eat Glatt or Mehadrin, or fancies
  // Italian or Moroccan, is asking one question — not three searches.
  //
  // Same shape as the hechsher filter at the top of the page: a button
  // that says how many are chosen, a list of checkboxes, comma-joined
  // into the one query parameter the backend now reads as "any of
  // these". Deliberately NOT a native <select multiple>, which on a
  // phone is a scrolling box nobody can operate.
  const MultiPicker = ({ show, label, aria, options, valueOf, current, onPick }) => {
    const [open, setOpen] = useState(false);
    const boxRef = React.useRef(null);
    useEffect(() => {
      if (!open) return undefined;
      const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
      const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
      document.addEventListener('mousedown', away);
      document.addEventListener('keydown', esc);
      return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
    }, [open]);
    if (!show || !options.length) return null;
    const picked = String(current || '').split(',').map((x) => x.trim()).filter(Boolean);
    const toggle = (v) => {
      const next = picked.some((x) => x.toLowerCase() === v.toLowerCase())
        ? picked.filter((x) => x.toLowerCase() !== v.toLowerCase())
        : [...picked, v];
      onPick(next.join(','));
    };
    const on = picked.length > 0;
    const summary = !on ? label
      : `${t(picked[0])}${picked.length > 1 ? ` +${picked.length - 1}` : ''}`;
    return (
      <div ref={boxRef} style={{ position: 'relative', marginBottom: 12 }}>
        <button
          type="button" aria-label={aria} aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          style={{ ...selectStyle, textAlign: 'start',
            borderColor: on ? COLORS.primary : COLORS.border,
            color: on ? COLORS.primary : COLORS.text }}
        >
          {summary}
        </button>
        {open && (
          <div style={{ position: 'absolute', zIndex: 30, insetInlineStart: 0, insetInlineEnd: 0, marginTop: 4,
            background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,.12)', maxHeight: 260, overflowY: 'auto', padding: 6 }}>
            {on && (
              <button type="button" onClick={() => { onPick(''); setOpen(false); }}
                style={{ width: '100%', textAlign: 'start', padding: '6px 8px', border: 'none', background: 'none',
                  cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: COLORS.muted }}>
                {t('Clear')}
              </button>
            )}
            {options.map((o) => {
              const v = valueOf(o);
              const checked = picked.some((x) => x.toLowerCase() === String(v).toLowerCase());
              return (
                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                  cursor: 'pointer', fontSize: 14, color: COLORS.text }}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(v)} style={{ cursor: 'pointer' }} />
                  <span style={{ flex: 1 }}>{t(v)}</span>
                  <span style={{ color: COLORS.muted, fontSize: 12 }}>{o.count}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // The kind filter holds one or two values — "Meat", or "Meat,Parve".
  // The dropdown shows the meat/dairy half; the checkbox owns the parve
  // half. Without this split, "Meat,Parve" matched no single option and
  // the dropdown fell back to reading "any" while a filter was active.
  const kindParts = String(filters.subtype || '').split(',').map((x) => x.trim()).filter(Boolean);
  const kindSide = kindParts.find((x) => !/^parve$/i.test(x)) || (kindParts.length ? 'Parve' : '');
  const kindHasParve = kindParts.some((x) => /^parve$/i.test(x));
  const bySubtype = (o) => o.subtype;
  const byValue = (o) => o.value;

  // I LINK DI VIAGGIO NON STANNO PIU' QUI. Stavano in cima a questa
  // colonna, poi in fondo, e in tutti e due i casi sul telefono finivano
  // DENTRO il pannello «Filtri», perche' li' dentro ci entra tutto il rail.
  // Adesso sono un componente loro, PlanYourTrip, reso in coda ai
  // risultati — fuori dai filtri su ogni schermo.

  // The category block is a FILTER now, not just a jump list, so it must not
  // depend on there being results: narrowing a country to one category can
  // return nothing, and the old `results.length > 0` gate took the tick-boxes
  // away at the exact moment you needed them to change your mind. It shows
  // whenever there are categories to offer, which the location-scoped list
  // supplies regardless of what the current selection returns.
  const showCategories = (Array.isArray(categories) && categories.length > 0)
    || (Array.isArray(results) && results.length > 0);

  return (
    <aside className="jc-list-rail">
      <div style={card}>
        <div style={head}>{t('Sort & filter')}</div>
        {/* Recommended by — only shown where someone actually vouches for
            something in view, so it never appears as an empty control. */}
        {recommenders.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <select
              aria-label={t('Filter by who recommends the place')}
              value={filters.recommended_by || ''}
              onChange={(e) => updateFilter('recommended_by', e.target.value)}
              style={{ width: '100%', padding: '9px 10px', fontSize: 14, borderRadius: 8, border: `1px solid ${COLORS.border}`, background: 'white', color: COLORS.text, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              <option value="">👍 {t('Recommended by: anyone')}</option>
              {recommenders.map((r) => (
                <option key={r.value} value={r.value}>{r.value} ({r.count})</option>
              ))}
            </select>
          </div>
        )}
        {/* Everything that narrows WITHIN a category lives here; the top row
            is location and category only. Each list is fetched for whatever
            the page is showing, so its counts are the counts the results
            return, and a list with nothing in it draws no control at all. */}
        <Picker
          show={isFood}
          label={`🍽️ ${t('Meat or dairy: any')}`}
          aria={t('Filter by meat or dairy')}
          options={foodKinds}
          valueOf={bySubtype}
          current={kindSide}
          onPick={(v) => {
            // Changing sides keeps the parve choice — someone who wanted
            // "meat or parve" still wants parve when they switch to dairy.
            if (!v) { updateFilter('subtype', ''); return; }
            const keepParve = kindHasParve && !/^parve$/i.test(v);
            updateFilter('subtype', keepParve ? `${v},Parve` : v);
          }}
        />
        {/* PARVE ALONGSIDE, NOT INSTEAD. A parve bakery is exactly what
            somebody wants before a meat meal, and asking for "Meat"
            used to hide it. This adds Parve to the chosen side; Meat
            and Dairy together is deliberately not offered, because
            nobody is looking for a place that is both at once. */}
        {isFood && /^(Meat|Dairy)$/i.test(kindSide) && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: -4, marginBottom: 12,
            fontSize: 13, color: COLORS.text, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={kindHasParve}
              onChange={(e) => {
                updateFilter('subtype', e.target.checked ? `${kindSide},Parve` : kindSide);
              }}
              style={{ cursor: 'pointer' }}
            />
            {t('include parve')}
          </label>
        )}
        <Picker
          show={isShul}
          label={`🕍 ${t('Nusach: any')}`}
          aria={t('Filter by nusach')}
          options={listed(nusachim, 'synagogue_shul')}
          valueOf={bySubtype}
          current={filters.subtype || ''}
          onPick={(v) => updateFilter('subtype', v)}
        />
        <Picker
          show={isOther}
          label={`🏷️ ${t('Kind: any')}`}
          aria={t('Filter by kind')}
          options={listed(otherSubtypes, otherType)}
          valueOf={bySubtype}
          current={filters.subtype || ''}
          onPick={(v) => updateFilter('subtype', v)}
        />
        <Picker
          show={isShul}
          label={`🤝 ${t('Community: any')}`}
          aria={t('Filter by community')}
          options={listed(communities, 'synagogue_shul', COMMUNITY_OPTIONS, byValue)}
          valueOf={byValue}
          current={filters.community || ''}
          onPick={(v) => updateFilter('community', v)}
        />
        <MultiPicker
          show={isFood}
          label={`🌍 ${t('Cuisine: any')}`}
          aria={t('Filter by cuisine')}
          options={cuisines}
          valueOf={byValue}
          current={filters.cuisine || ''}
          onPick={(v) => updateFilter('cuisine', v)}
        />
        <MultiPicker
          show
          label={`✡️ ${t('Kosher standard: any')}`}
          aria={t('Filter by kosher standard')}
          options={onlyIn(kosherLevels, isMikvah ? MIKVAH_LEVELS : KOSHER_LEVELS, byValue)}
          valueOf={byValue}
          current={filters.kosher_level || ''}
          onPick={(v) => updateFilter('kosher_level', v)}
        />
        <div style={{ marginBottom: 12 }}>
          <select
            aria-label={t('Sort the list')}
            value={filters.sort || 'category'}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'nearest') { if (onClosest) onClosest(); return; } // needs geolocation
              updateFilter('sort', v === 'category' ? '' : v);
            }}
            style={{ width: '100%', padding: '9px 10px', fontSize: 14, borderRadius: 8, border: `1px solid ${COLORS.border}`, background: 'white', color: COLORS.text, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <option value="category">🏷️ {t('Sort: Category')}</option>
            <option value="nearest">📍 {t('Sort: Closest to me')}</option>
            {/* City sort is pointless when results are already one city. */}
            {!city && <option value="city">🏙️ {t('Sort: City')}</option>}
            {/* Soonest first. Offered when the list is about dated
                programmes, or when the user is already sorting that way —
                for a restaurant list it would sort on a column nothing
                has filled in. */}
            {(DATED_TYPES.some((d) => String(filters.type || '').includes(d)) || filters.sort === 'date') && (
              <option value="date">📅 {t('Sort: Soonest first')}</option>
            )}
            <option value="name">🔤 {t('Sort: A → Z')}</option>
            <option value="name_desc">🔤 {t('Sort: Z → A')}</option>
          </select>
        </div>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: COLORS.muted, pointerEvents: 'none' }}>🔍</span>
          <input
            type="search"
            placeholder={t("Name, address, supervision…")}
            value={filters.search || ''}
            onChange={(e) => updateFilter('search', e.target.value)}
            style={{ width: '100%', padding: '9px 30px 9px 34px', fontSize: 14, border: `2px solid ${filters.search ? COLORS.primary : COLORS.border}`, borderRadius: 8, background: 'white', outline: 'none', boxSizing: 'border-box' }}
          />
          {filters.search && (
            <button
              onClick={() => updateFilter('search', '')}
              title={t("Clear filter")}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: COLORS.bg, border: 'none', width: 22, height: 22, borderRadius: '50%', cursor: 'pointer', fontSize: 12, color: COLORS.muted, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              ✕
            </button>
          )}
        </div>
        {(isSukkahSeason() || filters.has_sukkah === 'true') && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: COLORS.text, fontSize: 13, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={filters.has_sukkah === 'true'}
              onChange={(e) => updateFilter('has_sukkah', e.target.checked ? 'true' : '')}
              style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
            />
            🌿 {t('Has a sukkah')}
          </label>
        )}
        {(isPesachSeason() || filters.open_on_pesach === 'true') && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: COLORS.text, fontSize: 13, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={filters.open_on_pesach === 'true'}
              onChange={(e) => updateFilter('open_on_pesach', e.target.checked ? 'true' : '')}
              style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
            />
            🫓 {t('Open on Pesach')}
          </label>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: COLORS.text, fontSize: 13, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={filters.open_now === 'true'}
            onChange={(e) => updateFilter('open_now', e.target.checked ? 'true' : '')}
            style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
          />
          🕐 {t('Open now')}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: COLORS.text, fontSize: 13, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={filters.gluten_free === 'true'}
            onChange={(e) => updateFilter('gluten_free', e.target.checked ? 'true' : '')}
            style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
          />
          🌾 {t('Gluten-free options')}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: COLORS.text, fontSize: 13, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={filters.dine_in === 'true'}
            onChange={(e) => updateFilter('dine_in', e.target.checked ? 'true' : '')}
            style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
          />
          🍽️ {t('Dine-in')}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: COLORS.text, fontSize: 13, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={filters.takeaway === 'true'}
            onChange={(e) => updateFilter('takeaway', e.target.checked ? 'true' : '')}
            style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
          />
          🥡 {t('Takeaway')}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: COLORS.text, fontSize: 13, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={filters.wheelchair_accessible === 'true'}
            onChange={(e) => updateFilter('wheelchair_accessible', e.target.checked ? 'true' : '')}
            style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
          />
          ♿ {t('Wheelchair accessible')}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: COLORS.muted, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={filters.include_closed === 'true'}
            onChange={(e) => updateFilter('include_closed', e.target.checked ? 'true' : '')}
            style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
          />
          {t('Include closed / no longer kosher')}
        </label>
      </div>

      {showCategories && (
        <div style={card}>
          <CategoryToc
            variant="list"
            categories={categories}
            results={results}
            sortMode={sortMode}
            onJump={onJumpCategory}
            selectedTypes={pickedTypes}
            onToggleType={toggleType}
            onClearTypes={() => updateFilter('type', '')}
          />
        </div>
      )}

    </aside>
  );
}
